/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';
import * as Sentry from '@sentry/angular';
import { ConfigService } from './config.service';
import { HttpRetryableService } from './http-retryable.service';

export interface GetAllBackupListWithVersionResponse {
  success: boolean;
  data: {
    backupVersions: { id: number; version: number }[];
  };
}

export interface GetAllBackupListResponse {
  success: boolean;
  data: number[];
}

/**
 * REST-клиент бэкапа реестра на forwarding-service. Бэкап — это защищённый at-rest архив полного
 * реестра ТС заявки для восстановления на устройстве. Здесь только транспорт; КОГДА и ЧТО отправлять/
 * тянуть, решает {@code backup.service} (сравнение версий, разрешение конфликтов, очередь действий).
 */
@Injectable({
  providedIn: 'root'
})
export class BackupApiService {
  private readonly baseRestURL: string = '';

  constructor(
    protected http: HttpRetryableService,
    protected config: ConfigService
  ) {
    this.baseRestURL = config.baseRestURL;
  }

  /**
   * Выгрузка бэкапа заявки на сервер. Идемпотентность на уровне URL: путь содержит {@code idempotencyKey},
   * поэтому повтор той же логической выгрузки не создаёт дубль. Заголовок {@code Backup-Version} несёт
   * версию правок; в ответе сервер возвращает свою (её читает {@code backup.service}).
   * {@code overwrite=true} — принудительно записать локальную версию поверх серверной (разрешение
   * конфликта «оставить локальную»).
   */
  sendBackup(
    orderId: string,
    zipBlob: Blob,
    backupVersion: number,
    overwrite: boolean,
    idempotencyKey: string
  ): Observable<HttpResponse<void>> {
    const headers = {
      'Content-Type': 'application/octet-stream',
      'Backup-Version': backupVersion.toString()
    };
    return this.http.post<any>(
      `${this.baseRestURL}forwarding/backup/${orderId}/${idempotencyKey}`,
      zipBlob,
      {
        headers,
        params: { overwrite: overwrite.toString() },
        observe: 'response'
      }
    );
  }

  /** Идентификаторы заявок, по которым на сервере есть бэкап (для уборки устаревших локальных записей). */
  getAllBackupList() {
    return this.http
      .get<GetAllBackupListResponse>(`${this.baseRestURL}forwarding/backup/all`)
      .pipe(
        catchError(error => {
          Sentry.captureException(error);
          return throwError(() => new Error(error));
        })
      );
  }

  /** Список бэкапов с ВЕРСИЯМИ (id + version) — для сравнения с локальной версией и выбора направления
   *  синхронизации (тянуть с сервера / выгружать локальное / конфликт). */
  getAllBackupListWithVersion() {
    return this.http
      .get<GetAllBackupListWithVersionResponse>(
        `${this.baseRestURL}forwarding/backup/version/all`
      )
      .pipe(
        catchError(error => {
          Sentry.captureException(error);
          return throwError(() => new Error(error));
        })
      );
  }

  /** Скачивание архива бэкапа по идентификатору заявки (blob). Статус 204 — бэкапа для заявки нет. */
  getBackupById(id: number): Observable<any> {
    return this.http
      .get(`${this.baseRestURL}forwarding/backup/${id}`, {
        responseType: 'blob',
        observe: 'response'
      })
      .pipe(
        catchError(error => {
          Sentry.captureException(error);
          if (error.status === 204) {
            // console.log('Для этой заявки нет бэкапа');
          }
          // console.error('Ошибка при выполнении запроса:', error);
          return throwError(() => new Error(error));
        })
      );
  }
}
